import { useState } from 'react';
import { X, Megaphone, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
    const [category, setCategory] = useState<'general' | 'bug' | 'feature' | 'other'>('general');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!message.trim()) {
            setError('Please enter your feedback');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Get user if logged in
            const { data: { user } } = await supabase.auth.getUser();

            const { error: insertError } = await supabase
                .from('user_feedback')
                .insert({
                    user_id: user?.id || null,
                    category,
                    message: message.trim(),
                });

            if (insertError) throw insertError;

            setSuccess(true);
            setMessage('');
            setCategory('general');
        } catch (err: any) {
            setError(err.message || 'Failed to submit feedback');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
            <div className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                            <Megaphone className="size-5 text-cyan-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">Leave Feedback</h2>
                            <p className="text-xs text-white/50">Help us improve DECKSTR.GG</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white/60">
                        <X className="size-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {success ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="size-8 text-green-400" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                            <p className="text-white/60 text-sm">Your feedback has been received.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Category */}
                            <div>
                                <label className="block text-sm text-white/50 mb-2">Category</label>
                                <div className="flex gap-2 flex-wrap">
                                    {(['general', 'bug', 'feature', 'other'] as const).map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => setCategory(cat)}
                                            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${category === cat
                                                ? 'bg-cyan-500 text-white'
                                                : 'bg-white/10 text-white/60 hover:bg-white/20'
                                                }`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Message */}
                            <div>
                                <label className="block text-sm text-white/50 mb-2">Your Feedback</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Share your thoughts, report a bug, or suggest a feature..."
                                    rows={5}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 resize-none"
                                />
                                <p className="text-xs text-white/30 mt-1 text-right">{message.length}/1000</p>
                            </div>

                            {error && (
                                <p className="text-sm text-red-400">{error}</p>
                            )}

                            {/* Submit */}
                            <button
                                onClick={handleSubmit}
                                disabled={loading || !message.trim()}
                                className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <Loader2 className="size-5 animate-spin" />
                                ) : (
                                    <>
                                        <Megaphone className="size-5" />
                                        Submit Feedback
                                    </>
                                )}
                            </button>

                            <p className="text-xs text-white/30 text-center">
                                Feedback is one-way. For support, use "Message Mod Team".
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
